import React, { useState, useEffect } from 'react';
import { inventoryAPI } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Paper,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
  Collapse,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import {
  Science as ScienceIcon,
  QrCode as QrCodeIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  CheckCircle as ApprovedIcon,
  Warning as WarningIcon,
  HourglassEmpty as PendingIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';

const InventoryManagement = () => {
  const { org } = useOrg();

  const [activeTab, setActiveTab] = useState(0);
  const [batches, setBatches] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterBatch, setFilterBatch] = useState('');
  const [filterLot, setFilterLot] = useState('');
  const [filterExpiration, setFilterExpiration] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterLocation, setFilterLocation] = useState('');

  const itemsPerPage = 50;
  const [skuPage, setSkuPage] = useState(1);
  const [batchSkuPages, setBatchSkuPages] = useState({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBatchDetail, setSelectedBatchDetail] = useState(null);
  const [selectedSkuDetail, setSelectedSkuDetail] = useState(null);

  useEffect(() => {
    fetchInventory();
  }, [org]);

  useEffect(() => {
    setSkuPage(1);
  }, [activeTab, searchTerm, filterLot, filterLocation, filterStatus, filterExpiration]);

  const normalizeBatchStatus = (status) => {
    if (!status) return 'PENDING';
    const upper = String(status).toUpperCase();
    if (upper === 'FAILED') return 'FAILED';
    if (upper === 'PASSED' || upper === 'COMPLETED') return 'PASSED';
    if (upper === 'IN_PROGRESS' || upper === 'RECEIVING' || upper === 'PROCESSING') return 'PENDING';
    return 'PENDING';
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const invResponse = await inventoryAPI.getInventoryLevels({ organization: org?.toUpperCase() });
      const inventoryRaw = (invResponse.data || []).map((i) => ({
        id: i.id,
        batchId: i.batchId,
        productName: i.product?.name || '',
        productSku: i.product?.sku || i.product?.code || i.product?.skuCode || '',
        lotNumber: i.lotNumber,
        totalReceived: i.quantityReceived,
        totalAvailable: i.quantityAvailable,
        totalReserved: i.quantityReserved,
        totalAllocated: i.quantityAllocated,
        totalSold: Math.max(
          0,
          (i.quantityReceived || 0) -
            (i.quantityAvailable || 0) -
            (i.quantityReserved || 0) -
            (i.quantityAllocated || 0)
        ),
        expirationDate: i.expirationDate,
        receivedAt: i.receivedAt,
        qcStatus: normalizeBatchStatus(i.qcStatus || i.status),
        warehouse: i.warehouse || '',
        zone: i.zone || '',
        shelf: i.shelf || '',
        bin: i.bin || '',
        skus: []
      }));

      let batchesForOrg = inventoryRaw;

      try {
        const skuResponse = await inventoryAPI.getSKUs(org, 1000);
        const skuRows = (skuResponse.data || []).map((s) => {
          const tissueId =
            (s.tissueId || `${s.inventory?.lotNumber || ''}-${s.sku}` || s.sku || '').trim();
          let statusUpper = (s.status || '').toUpperCase();
          if (s.allocatedToOrderId && statusUpper !== 'ALLOCATED') {
            statusUpper = 'ALLOCATED';
          }
          return {
            id: s.id,
            tissueId,
            skuNumber: tissueId,
            baseSku: s.sku,
            batchId: s.inventory?.batchId || '',
            productName: s.product?.name || '',
            productCode: s.product?.sku || s.product?.code || s.product?.skuCode || '',
            lotNumber: s.inventory?.lotNumber || '',
            status: statusUpper,
            expirationDate: s.inventory?.expirationDate,
            manufacturingDate: s.inventory?.manufacturingDate,
            location: [s.warehouse, s.zone, s.shelf, s.bin].filter(Boolean).join('-') || '-',
            warehouse: s.warehouse || '',
            zone: s.zone || '',
            shelf: s.shelf || '',
            bin: s.bin || '',
            receivedDate: s.createdAt,
            orderNumber: s.allocatedToOrderId || null
          };
        });

        const allowedStatuses = ['AVAILABLE', 'ALLOCATED', 'SOLD'];
        const filteredSkuRows = skuRows.filter((sku) => allowedStatuses.includes(sku.status));

        setSkus(filteredSkuRows);

        const batchesWithSkus = batchesForOrg
          .filter((batch) => filteredSkuRows.some((sku) => sku.batchId === batch.batchId))
          .map((batch) => ({
            ...batch,
            skus: filteredSkuRows.filter((sku) => sku.batchId === batch.batchId)
          }));

        setBatches(batchesWithSkus);
        setBatchSkuPages((prev) => {
          const next = {};
          batchesWithSkus.forEach((batch) => {
            next[batch.id] = prev[batch.id] || 1;
          });
          return next;
        });
        setExpandedBatch(null);
        setSkuPage(1);
      } catch (skuError) {
        console.warn('Failed to fetch SKUs:', skuError);
        setSkus([]);
        setBatches(inventoryRaw);
        setBatchSkuPages({});
        setExpandedBatch(null);
        setSkuPage(1);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setBatches([]);
      setSkus([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const config = {
      PASSED: { color: 'success', icon: <ApprovedIcon fontSize="small" />, label: 'Approved' },
      PENDING: { color: 'warning', icon: <PendingIcon fontSize="small" />, label: 'Pending QC' },
      FAILED: { color: 'error', icon: <WarningIcon fontSize="small" />, label: 'Failed QC' }
    }[status] || {
      color: 'warning',
      icon: <PendingIcon fontSize="small" />,
      label: 'Pending QC'
    };

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const getSKUStatusChip = (status) => {
    const map = {
      AVAILABLE: { color: 'success', label: 'Available' },
      RESERVED: { color: 'warning', label: 'Reserved' },
      ALLOCATED: { color: 'info', label: 'Allocated' },
      SOLD: { color: 'default', label: 'Sold' },
      EXPIRED: { color: 'error', label: 'Expired' }
    };
    const config = map[status] || map.AVAILABLE;
    return (
      <Chip label={config.label} color={config.color} size="small" sx={{ fontWeight: 600 }} />
    );
  };

  const getExpirationStatus = (expirationDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day
    const expiry = new Date(expirationDate);
    expiry.setHours(0, 0, 0, 0); // Normalize to start of day
    const days = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));

    if (Number.isNaN(days)) return { color: 'default', days: '-' };
    if (days < 0) return { color: 'error', days };
    if (days <= 90) return { color: 'warning', days };
    return { color: 'success', days };
  };

  const getFilteredBatches = () =>
    (batches || []).filter((batch) => {
      const matchesSearch =
        !searchTerm ||
        batch.batchId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        batch.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        batch.lotNumber.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBatch = !filterBatch || batch.lotNumber.includes(filterBatch);
      const matchesLot = !filterLot || batch.lotNumber.includes(filterLot);
      const matchesStatus = filterStatus === 'ALL' || batch.qcStatus === filterStatus;

      let matchesExpiration = true;
      if (filterExpiration) {
        const expirationMonth = new Date(batch.expirationDate)
          .toISOString()
          .substring(0, 7);
        matchesExpiration = expirationMonth === filterExpiration;
      }

      return matchesSearch && matchesBatch && matchesLot && matchesStatus && matchesExpiration;
    });

  const getFilteredSKUs = () =>
    (skus || []).filter((sku) => {
      const matchesSearch =
        !searchTerm ||
        sku.skuNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sku.lotNumber.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesLot = !filterLot || sku.lotNumber.includes(filterLot);
      const matchesLocation = !filterLocation || sku.location.includes(filterLocation);
      const matchesStatus = filterStatus === 'ALL' || sku.status === filterStatus;

      let matchesExpiration = true;
      if (filterExpiration) {
        const expirationMonth = new Date(sku.expirationDate)
          .toISOString()
          .substring(0, 7);
        matchesExpiration = expirationMonth === filterExpiration;
      }

      return matchesSearch && matchesLot && matchesLocation && matchesStatus && matchesExpiration;
    });

  const uniqueLots = [...new Set((batches || []).map((b) => b.lotNumber))];
  const uniqueLocations = [...new Set((skus || []).map((s) => s.location))];
  const uniqueMonths = [
    ...new Set(
      (batches || [])
        .filter((b) => b.expirationDate)
        .map((b) => new Date(b.expirationDate).toISOString().substring(0, 7))
    )
  ].sort();

  const getPaginatedSKUs = (skuList) => {
    const start = (skuPage - 1) * itemsPerPage;
    return skuList.slice(start, start + itemsPerPage);
  };

  const getPaginatedBatchSKUs = (batchId) => {
    const batch = batches.find((b) => b.id === batchId);
    const list = batch?.skus || [];
    const page = batchSkuPages[batchId] || 1;
    const start = (page - 1) * itemsPerPage;
    return list.slice(start, start + itemsPerPage);
  };

  const handleBatchSkuPageChange = (batchId, page) => {
    setBatchSkuPages((prev) => ({ ...prev, [batchId]: page }));
  };

  const handleBatchExpandToggle = (batchId) => {
    setExpandedBatch((prev) => (prev === batchId ? null : batchId));
    setBatchSkuPages((prev) => ({ ...prev, [batchId]: 1 }));
  };

  const handleExportBatchesClick = () => {
    exportBatchesCSV();
  };

  const handleExportSKUView = () => {
    exportSKUViewCSV();
  };

  const exportSKUViewCSV = () => {
    try {
      const filtered = getFilteredSKUs();
      if (!filtered.length) {
        alert('No SKUs available to export for the current filters.');
        return;
      }

      const headers = [
        'Tissue ID',
        'Product Name',
        'Product Code/SKU',
        'Status',
        'Location',
        'Warehouse',
        'Expiration Date',
        'Manufacturing Date',
        'Order Number',
        'Batch ID'
      ];

      const rows = filtered.map((sku) =>
        [
          sku.skuNumber || '',
          sku.productName || '',
          sku.productCode || '',
          sku.status || '',
          sku.location || '',
          sku.warehouse || '',
          sku.expirationDate ? new Date(sku.expirationDate).toLocaleDateString() : '',
          sku.manufacturingDate ? new Date(sku.manufacturingDate).toLocaleDateString() : '',
          sku.orderNumber || '',
          sku.batchId || ''
        ]
          .map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`)
          .join(',')
      );

      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sku-view-export-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting SKU view CSV:', error);
      alert('Failed to export SKU view data. Please try again.');
    }
  };

  const exportBatchesCSV = () => {
    try {
      const batchesToExport = getFilteredBatches();
      if (!batchesToExport.length) {
        alert('No batches available to export for the current filters.');
        return;
      }

      const headers = [
        'Batch ID',
        'Product Name',
        'Product Code/SKU',
        'Donor ID (Lot Number)',
        'Total Received',
        'Total Available',
        'Total Reserved',
        'Total Allocated',
        'Total Sold',
        'Expiration Date',
        'Received Date',
        'QC Status',
        'Warehouse',
        'Tissue ID Count'
      ];

      const rows = batchesToExport.map((batch) => {
        const tissueIdCount = batch.skus?.length || 0;
        return [
          batch.batchId || '',
          batch.productName || '',
          batch.productSku || '',
          batch.lotNumber || '',
          batch.totalReceived || 0,
          batch.totalAvailable || 0,
          batch.totalReserved || 0,
          batch.totalAllocated || 0,
          batch.totalSold || 0,
          batch.expirationDate ? new Date(batch.expirationDate).toLocaleDateString() : '',
          batch.receivedAt ? new Date(batch.receivedAt).toLocaleDateString() : '',
          batch.qcStatus || 'PENDING',
          batch.warehouse || '',
          tissueIdCount
        ]
          .map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`)
          .join(',');
      });

      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch-export-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting batches CSV:', error);
      alert('Failed to export batch data. Please try again.');
    }
  };

  const handleViewBatchDetails = (batch) => {
    setSelectedBatchDetail(batch);
    setSelectedSkuDetail(null);
    setDetailDialogOpen(true);
  };

  const handleViewSKUDetails = (sku) => {
    setSelectedSkuDetail(sku);
    setSelectedBatchDetail(null);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedBatchDetail(null);
    setSelectedSkuDetail(null);
  };

  const filteredSKUs = getFilteredSKUs();
  const filteredBatches = getFilteredBatches();

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: 'Dashboard', path: `/internal/${org}/dashboard` },
          { label: 'Inventory Management', path: `/internal/${org}/inventory-management` }
        ]}
      />

      <Box
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
          p: 3,
          borderRadius: 2,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          boxShadow: '0 4px 12px rgba(3, 149, 130, 0.3)'
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
            Inventory Management
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            View and manage all active inventory — batch and SKU level tracking
          </Typography>
        </Box>
        <Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportBatchesClick}
              sx={{
                textTransform: 'none',
                bgcolor: 'white',
                color: '#039582',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              Export Batches
            </Button>
            <Button
              variant="contained"
              startIcon={<QrCodeIcon />}
              onClick={handleExportSKUView}
              sx={{
                textTransform: 'none',
                bgcolor: 'white',
                color: '#039582',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              Export SKUs
            </Button>
          </Stack>
        </Box>
      </Box>

      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScienceIcon fontSize="small" />
                  Batch View
                  <Chip label={filteredBatches.length} size="small" />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QrCodeIcon fontSize="small" />
                  SKU View
                  <Chip label={filteredSKUs.length} size="small" />
                </Box>
              }
            />
          </Tabs>
        </Box>

        <CardContent>
          <Card sx={{ mb: 3, bgcolor: alpha('#5c725c', 0.02), border: '1px solid #e2e8f0' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    placeholder={
                      activeTab === 0 ? 'Search batches, lots...' : 'Search tissue IDs, lots...'
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#64748b' }} />
                        </InputAdornment>
                      )
                    }}
                    size="small"
                  />
                </Grid>

                {activeTab === 0 && (
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Donor ID</InputLabel>
                      <Select
                        value={filterBatch}
                        onChange={(e) => setFilterBatch(e.target.value)}
                        label="Donor ID"
                      >
                        <MenuItem value="">All Donor IDs</MenuItem>
                        {uniqueLots.map((lot) => (
                          <MenuItem key={lot} value={lot}>
                            {lot}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Lot Number</InputLabel>
                    <Select
                      value={filterLot}
                      onChange={(e) => setFilterLot(e.target.value)}
                      label="Lot Number"
                    >
                      <MenuItem value="">All Lots</MenuItem>
                      {uniqueLots.map((lot) => (
                        <MenuItem key={lot} value={lot}>
                          {lot}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {activeTab === 1 && (
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Location</InputLabel>
                      <Select
                        value={filterLocation}
                        onChange={(e) => setFilterLocation(e.target.value)}
                        label="Location"
                      >
                        <MenuItem value="">All Locations</MenuItem>
                        {uniqueLocations.map((location) => (
                          <MenuItem key={location} value={location}>
                            {location}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Expiration Month</InputLabel>
                    <Select
                      value={filterExpiration}
                      onChange={(e) => setFilterExpiration(e.target.value)}
                      label="Expiration Month"
                    >
                      <MenuItem value="">All Months</MenuItem>
                      {uniqueMonths.map((month) => (
                        <MenuItem key={month} value={month}>
                          {new Date(month).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long'
                          })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="ALL">All Status</MenuItem>
                      {activeTab === 0 ? (
                        <>
                          <MenuItem value="PASSED">Approved</MenuItem>
                          <MenuItem value="PENDING">Pending QC</MenuItem>
                          <MenuItem value="FAILED">Failed QC</MenuItem>
                        </>
                      ) : (
                        <>
                          <MenuItem value="AVAILABLE">Available</MenuItem>
                          <MenuItem value="RESERVED">Reserved</MenuItem>
                          <MenuItem value="ALLOCATED">Allocated</MenuItem>
                          <MenuItem value="SOLD">Sold</MenuItem>
                          <MenuItem value="EXPIRED">Expired</MenuItem>
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {activeTab === 0 ? (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#5c725c', 0.05) }}>
                    <TableCell width={56} />
                    <TableCell>Batch</TableCell>
                    <TableCell>Donor ID</TableCell>
                    <TableCell>Expiration</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Available</TableCell>
                    <TableCell>Allocated</TableCell>
                    <TableCell>Sold</TableCell>
                    <TableCell>QC Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                        <Typography variant="body1">Loading inventory…</Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredBatches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                        <ScienceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          No batches found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Adjust your filters or refresh to try again.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBatches.map((batch) => {
                      const isExpanded = expandedBatch === batch.id;
                      const paginatedSkus = getPaginatedBatchSKUs(batch.id);
                      const allSkus = batch.skus || [];
                      const expirationStatus = getExpirationStatus(batch.expirationDate);

                      return (
                        <React.Fragment key={batch.id}>
                          <TableRow hover selected={isExpanded}>
                            <TableCell padding="checkbox">
                              <Tooltip title={isExpanded ? 'Collapse batch' : 'Expand batch'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleBatchExpandToggle(batch.id)}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {batch.productName || 'Unknown Product'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                                  {batch.productSku || '-'}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                              {batch.lotNumber}
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {batch.expirationDate
                                    ? new Date(batch.expirationDate).toLocaleDateString()
                                    : '-'}
                                </Typography>
                                {batch.expirationDate && (
                                  <Chip
                                    label={`${expirationStatus.days} days`}
                                    size="small"
                                    color={expirationStatus.color}
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                              {[batch.warehouse, batch.zone, batch.shelf, batch.bin]
                                .filter(Boolean)
                                .join('-') || '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={batch.totalAvailable ?? 0}
                                size="small"
                                sx={{
                                  bgcolor: alpha('#10b981', 0.1),
                                  color: '#10b981',
                                  fontWeight: 700
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                                {batch.totalAllocated ?? 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                {batch.totalSold ?? 0}
                              </Typography>
                            </TableCell>
                            <TableCell>{getStatusChip(batch.qcStatus)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="View batch details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewBatchDetails(batch)}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell colSpan={10} sx={{ p: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 3, bgcolor: alpha('#5c725c', 0.03) }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                      gap: 1.5,
                                      mb: 2
                                    }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      Tissue IDs ({allSkus.length})
                                    </Typography>
                                  </Box>

                                  {!allSkus.length ? (
                                    <Alert severity="info">
                                      No tissue IDs have been generated for this batch yet.
                                    </Alert>
                                  ) : (
                                    <>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Tissue ID</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Location</TableCell>
                                            <TableCell>Expiration</TableCell>
                                            <TableCell>Order #</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {paginatedSkus.map((sku) => (
                                            <TableRow key={sku.id} hover>
                                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                {sku.skuNumber}
                                              </TableCell>
                                              <TableCell>{getSKUStatusChip(sku.status)}</TableCell>
                                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                {sku.location}
                                              </TableCell>
                                              <TableCell>
                                                {sku.expirationDate
                                                  ? new Date(sku.expirationDate).toLocaleDateString()
                                                  : '-'}
                                              </TableCell>
                                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                {sku.orderNumber || '-'}
                                              </TableCell>
                                              <TableCell align="right">
                                                <Tooltip title="View SKU details">
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => handleViewSKUDetails({ ...sku, batch })}
                                                    sx={{ color: '#2563eb' }}
                                                  >
                                                    <ViewIcon fontSize="inherit" />
                                                  </IconButton>
                                                </Tooltip>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                      {allSkus.length > itemsPerPage && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                          <Pagination
                                            size="small"
                                            color="primary"
                                            count={Math.ceil(allSkus.length / itemsPerPage)}
                                            page={batchSkuPages[batch.id] || 1}
                                            onChange={(_, page) => handleBatchSkuPageChange(batch.id, page)}
                                          />
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha('#5c725c', 0.05) }}>
                      <TableCell>Tissue ID</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Product Code/SKU</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Expiration Date</TableCell>
                      <TableCell>Manufacturing Date</TableCell>
                      <TableCell>Order #</TableCell>
                      <TableCell align="right">Actions/Edit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                          <Typography variant="body1">Loading inventory…</Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredSKUs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                          <QrCodeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            No SKUs found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Adjust your filters or refresh to try again.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      getPaginatedSKUs(filteredSKUs).map((sku) => {
                        const expirationStatus = getExpirationStatus(sku.expirationDate);
                        return (
                          <TableRow key={sku.id} hover>
                            <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                              {sku.skuNumber}
                            </TableCell>
                            <TableCell>{sku.productName}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                              {sku.productCode || '-'}
                            </TableCell>
                            <TableCell>{getSKUStatusChip(sku.status)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  {sku.location}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {sku.expirationDate
                                    ? new Date(sku.expirationDate).toLocaleDateString()
                                    : '-'}
                                </Typography>
                                {!Number.isNaN(expirationStatus.days) && (
                                  <Chip
                                    label={`${expirationStatus.days} days`}
                                    size="small"
                                    color={expirationStatus.color}
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              {sku.manufacturingDate
                                ? new Date(sku.manufacturingDate).toLocaleDateString()
                                : '-'}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              {sku.orderNumber || '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View SKU details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewSKUDetails(sku)}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit SKU">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewSKUDetails(sku)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {filteredSKUs.length > itemsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    color="primary"
                    count={Math.ceil(filteredSKUs.length / itemsPerPage)}
                    page={skuPage}
                    onChange={(_, page) => setSkuPage(page)}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedBatchDetail ? 'Batch Details' : selectedSkuDetail ? 'SKU Details' : 'Details'}
        </DialogTitle>
        <DialogContent dividers>
          {selectedBatchDetail ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Batch Information
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Batch ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {selectedBatchDetail.batchId}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Product Name
                      </Typography>
                      <Typography variant="body1">{selectedBatchDetail.productName}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Product SKU
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                        {selectedBatchDetail.productSku || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Donor ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                        {selectedBatchDetail.lotNumber}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Inventory Summary
                  </Typography>
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Total Received</Typography>
                      <Chip label={selectedBatchDetail.totalReceived ?? 0} size="small" />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Available</Typography>
                      <Chip
                        label={selectedBatchDetail.totalAvailable ?? 0}
                        size="small"
                        color="success"
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Reserved</Typography>
                      <Chip
                        label={selectedBatchDetail.totalReserved ?? 0}
                        size="small"
                        color="warning"
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Allocated</Typography>
                      <Chip
                        label={selectedBatchDetail.totalAllocated ?? 0}
                        size="small"
                        color="info"
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Sold</Typography>
                      <Chip label={selectedBatchDetail.totalSold ?? 0} size="small" />
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <ScheduleIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                    Dates
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Expiration Date
                      </Typography>
                      <Typography variant="body1">
                        {selectedBatchDetail.expirationDate
                          ? new Date(selectedBatchDetail.expirationDate).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Received Date
                      </Typography>
                      <Typography variant="body1">
                        {selectedBatchDetail.receivedAt
                          ? new Date(selectedBatchDetail.receivedAt).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <LocationIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                    Location
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Warehouse
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedBatchDetail.warehouse || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Zone / Shelf / Bin
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {[selectedBatchDetail.zone, selectedBatchDetail.shelf, selectedBatchDetail.bin]
                          .filter(Boolean)
                          .join('-') || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          ) : selectedSkuDetail ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    SKU Information
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Tissue ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {selectedSkuDetail.skuNumber}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Product Name
                      </Typography>
                      <Typography variant="body1">{selectedSkuDetail.productName || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Lot Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                        {selectedSkuDetail.lotNumber || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <LocationIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                    Location & Dates
                  </Typography>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Location
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedSkuDetail.location}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Warehouse
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedSkuDetail.warehouse || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Expiration Date
                      </Typography>
                      <Typography variant="body1">
                        {selectedSkuDetail.expirationDate
                          ? new Date(selectedSkuDetail.expirationDate).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Received Date
                      </Typography>
                      <Typography variant="body1">
                        {selectedSkuDetail.receivedDate
                          ? new Date(selectedSkuDetail.receivedDate).toLocaleDateString()
                          : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Order Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                        {selectedSkuDetail.orderNumber || '-'}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDetailDialog} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryManagement;

